// src/scene/tokens.ts
/**
 * Design Tokens System
 *
 * Centralized design language for the entire 3D portfolio.
 * Premium, restrained palette with consistent spacing and typography.
 */

// ============================================================================
// COLOR PALETTE
// ============================================================================

export const palette = {
  // Backgrounds - dark, desaturated blues
  background: {
    deep: '#050508',      // Deepest background (environment sphere)
    base: '#0a0a10',      // Base scene background
    elevated: '#0f0f18',  // Elevated surfaces (panels)
    surface: '#14141f',   // Surface elements (cards)
    overlay: '#1a1a28',   // Overlays, modals
  },

  // Primary accent - cool blue (restrained, not neon)
  primary: {
    50: 'rgba(100, 140, 220, 0.05)',
    100: 'rgba(100, 140, 220, 0.10)',
    200: 'rgba(100, 140, 220, 0.20)',
    300: 'rgba(100, 140, 220, 0.40)',
    400: 'rgba(100, 140, 220, 0.60)',
    500: '#648cdc',       // Primary solid
    600: '#5070b8',
    700: '#3c5494',
  },

  // Secondary accent - warm highlight
  secondary: {
    300: 'rgba(220, 180, 140, 0.40)',
    400: 'rgba(220, 180, 140, 0.60)',
    500: '#dcb48c',
  },

  // Success - muted green
  success: {
    300: 'rgba(100, 180, 120, 0.40)',
    400: 'rgba(100, 180, 120, 0.60)',
    500: '#64b478',
  },

  // Text hierarchy
  text: {
    primary: 'rgba(255, 255, 255, 0.95)',
    secondary: 'rgba(255, 255, 255, 0.70)',
    tertiary: 'rgba(255, 255, 255, 0.50)',
    muted: 'rgba(255, 255, 255, 0.30)',
    accent: '#648cdc',
  },

  // Borders
  border: {
    subtle: 'rgba(255, 255, 255, 0.04)',
    light: 'rgba(255, 255, 255, 0.08)',
    medium: 'rgba(255, 255, 255, 0.12)',
    strong: 'rgba(255, 255, 255, 0.20)',
    accent: 'rgba(100, 140, 220, 0.40)',
    accentStrong: 'rgba(100, 140, 220, 0.60)',
  },

  // Glass/translucent surfaces
  glass: {
    background: 'rgba(15, 15, 24, 0.85)',
    backgroundActive: 'rgba(20, 20, 32, 0.92)',
    border: 'rgba(255, 255, 255, 0.06)',
    highlight: 'rgba(255, 255, 255, 0.03)',
  },

  // Project-specific accents (restrained versions)
  projects: {
    blue: 'rgba(100, 140, 220, 0.50)',
    green: 'rgba(100, 180, 120, 0.50)',
    orange: 'rgba(220, 150, 100, 0.50)',
    purple: 'rgba(160, 120, 200, 0.50)',
  },
} as const

// ============================================================================
// TYPOGRAPHY
// ============================================================================

export const typography = {
  // Font sizes in pixels (for UIKit)
  size: {
    xs: 10,
    sm: 12,
    md: 14,
    lg: 18,
    xl: 24,
    '2xl': 32,
    '3xl': 42,
  },

  // Line heights
  leading: {
    tight: 1.2,
    normal: 1.4,
    relaxed: 1.6,
  },

  // Font weights (numeric values for @react-three/uikit compatibility)
  weight: {
    normal: 400 as const,
    medium: 500 as const,
    semibold: 600 as const,
    bold: 700 as const,
  },
} as const

// ============================================================================
// SPACING
// ============================================================================

export const spacing = {
  // Base spacing scale (pixels for UIKit)
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  '2xl': 32,
  '3xl': 48,

  // Semantic spacing
  panelPadding: 20,
  cardPadding: 16,
  sectionGap: 24,
  itemGap: 12,
  inlineGap: 8,
} as const

// ============================================================================
// RADII
// ============================================================================

export const radii = {
  sm: 6,
  md: 10,
  lg: 14,
  xl: 18,
  '2xl': 24,
  full: 9999,
} as const

// ============================================================================
// ELEVATION (Z-depth for 3D transforms)
// ============================================================================

export const elevation = {
  flat: 0,
  raised: 2,
  floating: 4,
  overlay: 8,
  modal: 12,
} as const

// ============================================================================
// PANEL DIMENSIONS
// ============================================================================

export const panelSize = {
  // Width tokens
  width: {
    sm: 240,
    md: 300,
    lg: 360,
    xl: 420,
  },

  // Card dimensions
  card: {
    width: 180,
    minHeight: 120,
  },

  // Max content width
  maxContent: 320,
} as const

// ============================================================================
// ANIMATION TOKENS
// ============================================================================

export const animation = {
  // Spring configs for @react-spring/three
  spring: {
    // Gentle, premium feel
    gentle: { tension: 120, friction: 14 },
    // Responsive but smooth
    responsive: { tension: 170, friction: 20 },
    // Snappy for micro-interactions
    snappy: { tension: 300, friction: 24 },
    // Slow, cinematic
    slow: { tension: 80, friction: 18 },
  },

  // Durations in ms
  duration: {
    fast: 150,
    normal: 250,
    slow: 400,
    xslow: 600,
  },

  // Hover/interaction transforms
  hover: {
    lift: -2,           // Y translation on hover (pixels in UIKit)
    scale: 1.02,        // Scale factor on hover
    tiltDegrees: 2,     // Max tilt angle
  },

  // Active state transforms
  active: {
    scale: 1.04,
    floatAmplitude: 0.015,  // Meters for 3D float
    floatSpeed: 1.5,        // Multiplier
  },
} as const

// ============================================================================
// CAMERA & 3D WORLD
// ============================================================================

export const world = {
  // Real-world scale in meters
  scale: 1,

  // Camera defaults
  camera: {
    fov: 50,
    near: 0.1,
    far: 100,
    defaultHeight: 1.5,  // Eye level in meters
  },

  // Ground plane
  ground: {
    y: -0.9,            // Ground level
    radius: 15,         // Ground plane size
  },

  // Environment sphere
  environment: {
    radius: 30,
  },

  // Transition damping
  damping: {
    camera: 4,          // Lerp speed for camera
    cameraTransition: 2.5,  // Slower during transitions
  },
} as const

// ============================================================================
// RESPONSIVE BREAKPOINTS
// ============================================================================

export const breakpoints = {
  mobile: {
    maxWidth: 768,
    maxAspect: 0.85,
  },
  tablet: {
    maxWidth: 1024,
    maxAspect: 1.4,
  },
  // Desktop is default/fallback
} as const

// ============================================================================
// RESPONSIVE SCALE FACTORS
// ============================================================================

export const responsiveScale = {
  mobile: {
    ui: 0.75,
    camera: 1.4,        // Pull camera back
    panelWidth: 0.8,
    fontSize: 0.85,
  },
  tablet: {
    ui: 0.88,
    camera: 1.2,
    panelWidth: 0.9,
    fontSize: 0.92,
  },
  desktop: {
    ui: 0.95,
    camera: 1,
    panelWidth: 1,
    fontSize: 1,
  },
} as const

// ============================================================================
// EXPORT TYPE HELPERS
// ============================================================================

export type Palette = typeof palette
export type Typography = typeof typography
export type Spacing = typeof spacing
export type Radii = typeof radii
export type Breakpoint = 'mobile' | 'tablet' | 'desktop'
